import { IModalPlugin } from '@/plugins/modalPlugin/modalPlugin';
import { LocalizationServices } from '@/services/LocalizationServices';
import EditMaterialCompositionModal from '@/views/calculator/modals/editMaterialCompositionModal.vue';
import InfoModal from '@/components/modals/infoModal.vue';
import * as OM from '@/Model';

export interface MacroFillOptions {
    /** The macro-category row (parentName == ""). */
    macro: OM.PercentageCompositionValues;
    /** Its children: the rows whose parentName is the macro's name. */
    children: OM.PercentageCompositionValues[];
    availableMaterials: OM.RawMaterialConfigurationVM[];
    allCountries: OM.TextIdentifier[];
    isReadOnly: boolean;
    estimationIdentifier: string;
    /** The garment's own client call. The caller knows whether this is a shoe or a bag; this module does not. */
    save: (model: OM.UpdateMaterialCompositionVM) => Promise<any>;
    /** Reload the step. Called after a successful save, with the modal already closed. */
    onSaved: () => void;
}

/**
 * The composition the children agree on, or null.
 *
 * The macro stores nothing of its own: what the modal shows is the children's actual state, so it can
 * never go stale. Pre-filling when they DISAGREE would claim a composition that is not in place, so it
 * requires every child to be filled and to match.
 *
 * Materials are compared by identifier, never by name: two catalogue rows can share a name.
 */
export function sharedComposition(children: OM.PercentageCompositionValues[]): OM.PercentageCompositionValues {
    if(children.length == 0 || children.some(x => !x.materialsAssigned))
        return null;

    let first = children[0];

    let countryOf = (row: OM.PercentageCompositionValues) =>
        row.selectedCountry != null ? row.selectedCountry.identifier : "";

    let sameAsFirst = (row: OM.PercentageCompositionValues) => {
        if(row.selectedMaterials.length != first.selectedMaterials.length)
            return false;

        let matches = first.selectedMaterials.every(a => row.selectedMaterials.some(b =>
            b.material.identifier == a.material.identifier && b.percentage == a.percentage));

        return matches && countryOf(row) == countryOf(first);
    };

    return children.every(sameAsFirst) ? first : null;
}

/**
 * Opens the per-element composition modal on a whole macro-category.
 *
 * The modal itself is unchanged and garment-agnostic: it takes an element name and hands a payload back.
 * What differs per garment is the client call, which arrives as opts.save.
 */
export function openMacroCompositionModal(modal: IModalPlugin, opts: MacroFillOptions) {
    let shared = sharedComposition(opts.children);
    let alreadyFilled = opts.children.filter(x => x.materialsAssigned).length;

    modal.show(EditMaterialCompositionModal, {
        elementName: opts.macro.name,
        availableMaterials: opts.availableMaterials,
        allCountries: opts.allCountries,
        initialMaterials: shared != null ? shared.selectedMaterials : [],
        initialCountryIdentifier: shared != null && shared.selectedCountry != null ? shared.selectedCountry.identifier : "",
        initialNoMaterial: shared != null && shared.selectedMaterials.length == 0,
        isReadOnly: opts.isReadOnly,
        saveCallback: (payload) => {
            let save = () => {
                let model = new OM.UpdateMaterialCompositionVM();
                model.identifier = opts.estimationIdentifier;
                model.name = opts.macro.name;
                model.noMaterial = payload.noMaterial;
                model.materials = payload.noMaterial ? [] : payload.materials;
                model.selectedCountry = payload.noMaterial
                    ? new OM.TextIdentifier()
                    : opts.allCountries.filter(x => x.identifier == payload.countryIdentifier)[0];

                return opts.save(model)
                .then(() => {
                    modal.closeLast();
                    opts.onSaved();
                })
            };

            if(alreadyFilled == 0)
                return save();

            // The modals are stacked, so the confirmation must close ITSELF before save() closes the
            // composition modal underneath it -- otherwise closeLast() closes the wrong one.
            return new Promise<void>((resolve, reject) => {
                modal.show(InfoModal, {
                    text: LocalizationServices.getLocalizedValue("Overwrite the sub-elements?", "Overwrite the sub-elements?"),
                    subtitle: LocalizationServices.getLocalizedValue(
                        "Sub-elements already filled that will be overwritten:",
                        "Sub-elements already filled that will be overwritten:") + " " + alreadyFilled,
                    confirm: LocalizationServices.getLocalizedValue("Confirm", "Confirm"),
                    deny: LocalizationServices.getLocalizedValue("Cancel", "Cancel"),
                    confirmCb: () => {
                        modal.closeLast();
                        save().then(resolve).catch(reject);
                    },
                    denyCb: () => {
                        modal.closeLast();
                        resolve();
                    }
                })
            })
        }
    })
}
